package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.PrimaryButtonStyle
import com.iregained.site.components.sections.home.styles.SecondaryButtonStyle
import com.iregained.site.components.widgets.AnimatedHeroText
import com.iregained.site.components.widgets.SharedHeroActionLink
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.math.min
import kotlin.time.Duration.Companion.milliseconds

/**
 * CMS IMAGE UPLOAD SPECS FOR THIS SECTION:
 * @field headerImagePath:
 * - Aspect Ratio: 16:9 (Widescreen)
 * - Recommended Dimensions: 1920x1080px (Full HD)
 * - Max File Size: Under 300KB recommended (Max 500KB, WebP highly preferred)
 * - Behavior: Image is full-bleed and will be cropped to fit (ObjectFit.Cover).
 * - Mobile Safe Zone: Keep the primary subject (e.g., the device) framed in the exact center horizontal third of the image so it remains visible when cropped on vertical mobile screens.
 */
data class HomeHeroSectionData(
    val sectionId: String? = null,
    val overline: String,
    val rotatingOverlines: List<String>,
    val headline: String,
    val rotatingHeadlines: List<String>,
    val description: String,
    val primaryButtonText: String,
    val primaryButtonPath: String,
    val secondaryButtonText: String,
    val secondaryButtonPath: String,
    val headerImagePath: String
) {
    init {
        require(rotatingOverlines.isNotEmpty()) { "rotatingOverlines must not be empty" }
        require(rotatingHeadlines.isNotEmpty()) { "rotatingHeadlines must not be empty" }
    }
}

val defaultHeroSectionData = HomeHeroSectionData(
    sectionId = "home-header",
    // EXACT PAIR 1
    overline = "Redefining",
    headline = "Rehabilitation Landscape",

    // REMAINING PAIRS FOR ROTATION
    rotatingOverlines = listOf("Enabling", "Restoring", "Transforming"),
    rotatingHeadlines = listOf("Better Clinical Outcomes", "Patient Quality of Life", "Hand Therapy"),

    description = "The MyHand<sup>®</sup> System is a ground breaking technology that enables clinicians achieve improved functional outcomes consistently and empowers stroke and brain injury survivors regain hand-function through clinically-proven, neuro-plasticity driven therapy.",
    primaryButtonText = "Request a Demo",
    primaryButtonPath = "/contact",
    secondaryButtonText = "How it Works",
    secondaryButtonPath = "/product#product-demonstration-section",
    headerImagePath = Res.Images.Home.HEADER_BANNER
)

// Creates a calm, organic 15-second zoom loop for the background
// Creates a calm, organic 15-second zoom loop for the background
val BreathingBackgroundKeyframes = Keyframes {
    0.percent { Modifier.scale(1) }
    50.percent { Modifier.scale(1.06) }
    100.percent { Modifier.scale(1) }
}

val CinematicTextRevealKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY(30.px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

@Composable
fun Modifier.staggeredReveal(delayMs: Int): Modifier = this.animation(
    CinematicTextRevealKeyframes.toAnimation(
        duration = 0.8.s,
        timingFunction = AnimationTimingFunction.EaseOut,
        delay = delayMs.ms,
        fillMode = AnimationFillMode.Both
    )
)

// --- 2. MAIN COMPONENT LAYOUT ---
@Composable
fun HomeHeroSection(data: HomeHeroSectionData = defaultHeroSectionData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-header"
    val breakpoint = LocalBreakpoint.current
    val isMobile = breakpoint < Res.Breakpoints.MD

    // Combine into guaranteed pairs
    val overlines = remember(data.overline, data.rotatingOverlines) {
        listOf(data.overline) + data.rotatingOverlines
    }
    val headlines = remember(data.headline, data.rotatingHeadlines) {
        listOf(data.headline) + data.rotatingHeadlines
    }

    // --- UNIFIED ANIMATION STATE ---
//    var currentIndex by remember { mutableStateOf(0) }
//    var displayedHeadline by remember { mutableStateOf(headlines.firstOrNull() ?: "") }
//    var isOverlineVisible by remember { mutableStateOf(true) }
//    var showCursor by remember { mutableStateOf(true) }

    // Blinking cursor loop (runs independently)
//    LaunchedEffect(Unit) {
//        while (true) {
//            delay(500.milliseconds)
//            showCursor = !showCursor
//        }
//    }

    // Unified Synchronized Animation Sequence
//    LaunchedEffect(pairCount) {
//        if (pairCount <= 1) return@LaunchedEffect // Nothing to rotate
//
//        // Start from the fully typed out initial state
//        while (true) {
//            val currentHeadline = headlines[currentIndex]
//
//            // 1. Hold Phase (Show both texts)
//            delay(3500.milliseconds)
//
//            // 2. Delete Phase
//            isOverlineVisible = false // Trigger overline fade-out & slide up
//            for (i in currentHeadline.length downTo 0) {
//                displayedHeadline = currentHeadline.substring(0, i)
//
//                // OPTIMIZATION: Increased from 40ms to 60ms to prevent scroll-blocking
//                delay(60.milliseconds)
//            }
//
//            // Brief pause while both are empty
//            delay(300.milliseconds)
//
//            // 3. Increment Phase
//            currentIndex = (currentIndex + 1) % pairCount
//            val nextHeadline = headlines[currentIndex]
//
//            // 4. Reveal & Type Phase
//            isOverlineVisible = true // Trigger overline fade-in & slide down from top
//            for (i in 1..nextHeadline.length) {
//                displayedHeadline = nextHeadline.substring(0, i)
//
//                // OPTIMIZATION: Increased from 80ms to 120ms to prevent scroll-blocking
//                delay(120.milliseconds)
//            }
//        }
//    }

    Box(
        modifier = containerModifier(targetId), contentAlignment = Alignment.CenterStart
    ) {
        // LAYER 1: Breathing Background Image
        Box(modifier = backgroundModifier()) {
            Image(
                src = data.headerImagePath,
                alt = "MyHand System Environment",
                modifier = Modifier.unextractable().fillMaxSize().objectFit(ObjectFit.Cover)
            )
        }

        // LAYER 2: Uniform Cinematic Overlay
        Box(modifier = overlayModifier())

        // LAYER 3: Main Content Grid
        Column(
            modifier = contentGridModifier(isMobile),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            // --- THE UNIFIED GLASS CARD ---
            Column(
                modifier = glassCardModifier(isMobile), horizontalAlignment = Alignment.CenterHorizontally
            ) {

                AnimatedHeroText(
                    headlines = headlines,
                    overlines = overlines,
                    overlineModifier = overlineModifier(isMobile).staggeredReveal(delayMs = 200),
                    headlineModifier = headlineModifier(isMobile).staggeredReveal(delayMs = 400).fillMaxWidth()
                )

                // DYNAMIC OVERLINE (Fade & Slide from top effect, synced to headline)
//                Box(modifier = Modifier.staggeredReveal(delayMs = 200)) {
//                    SpanText(
//                        text = overlines[currentIndex], modifier = overlineModifier(isMobile)
//                        // CSS transition handles the smooth loop effect (increased to 0.8s for visibility)
//                        .styleModifier {
//                            property(
//                                "transition",
//                                "opacity 0.8s ease-in-out, transform 0.8s ease-in-out"
//                            )
//                        }.opacity(if (isOverlineVisible) 1 else 0)
//                        .translateY(if (isOverlineVisible) 0.px else (-15).px))
//                }

                // DYNAMIC HEADLINE (Typewriter effect, synced to overline)
//                Div(
//                    attrs = headlineModifier(isMobile).staggeredReveal(delayMs = 400).toAttrs()
//                ) {
//                    Text(displayedHeadline)
//                    Span(
//                        attrs = Modifier.opacity(if (showCursor) 1 else 0).toAttrs()
//                    ) {
//                        Text("|")
//                    }
//                }

                Div(
                    attrs = descriptionModifier(isMobile).staggeredReveal(delayMs = 600).toAttrs {
                        ref { htmlElement ->
                            htmlElement.innerHTML = data.description
                            onDispose {}
                        }
                    })

                Row(
                    modifier = actionRibbonModifier(isMobile).staggeredReveal(delayMs = 800),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    SharedHeroActionLink(
                        text = data.primaryButtonText,
                        path = data.primaryButtonPath,
                        styleModifier = PrimaryButtonStyle.toModifier()
                    )
                    SharedHeroActionLink(
                        text = data.secondaryButtonText,
                        path = data.secondaryButtonPath,
                        styleModifier = SecondaryButtonStyle.toModifier()
                    )
                }
            }
        }
    }
}

// --- 3. EXTRACTED STYLE MODIFIERS ---
private fun containerModifier(targetId: String) =
    Modifier.id(targetId).fillMaxWidth().styleModifier { property("min-height", "90dvh") }.position(Position.Relative)
        .overflow(Overflow.Hidden)

@Composable
private fun backgroundModifier() = Modifier.fillMaxSize().position(Position.Absolute).zIndex(1).animation(
    BreathingBackgroundKeyframes.toAnimation(
        duration = 15.s,
        timingFunction = AnimationTimingFunction.EaseInOut,
        iterationCount = AnimationIterationCount.Infinite
    )
)

private fun overlayModifier() =
    Modifier.fillMaxSize().position(Position.Absolute).zIndex(2).backgroundColor(rgba(15, 23, 42, 0.5f))

private fun contentGridModifier(isMobile: Boolean) =
    Modifier.fillMaxSize().padding(leftRight = 5.percent, topBottom = if (isMobile) 30.px else 60.px).zIndex(3)

private fun glassCardModifier(isMobile: Boolean) = Modifier.maxWidth(800.px).backgroundColor(rgba(255, 255, 255, 0.08f))
    .styleModifier { property("backdrop-filter", "blur(16px)") }
    .border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.3f)).borderRadius(24.px)
    .padding(if (isMobile) 24.px else 48.px)

private fun overlineModifier(isMobile: Boolean) =
    Modifier.color(rgb(255, 255, 255)).fontSize(if (isMobile) 12.px else 18.px).fontWeight(FontWeight.Bold)
        .letterSpacing(1.5.px).textTransform(TextTransform.Uppercase).textAlign(TextAlign.Center)
        .margin(bottom = if (isMobile) 10.px else 20.px)

private fun headlineModifier(isMobile: Boolean) =
    Modifier.color(rgb(255, 255, 255)).fontSize(if (isMobile) 25.px else 50.px).fontWeight(FontWeight.Bold)
        .lineHeight(1.1).margin(bottom = if (isMobile) 16.px else 24.px).textAlign(TextAlign.Center)

private fun descriptionModifier(isMobile: Boolean) =
    Modifier.color(rgba(255, 255, 255, 0.85f)).fontSize(if (isMobile) 16.px else 20.px).fontWeight(FontWeight.Medium)
        .lineHeight(1.6).margin(bottom = if (isMobile) 24.px else 40.px).textAlign(TextAlign.Center)

private fun actionRibbonModifier(isMobile: Boolean) =
    Modifier.gap(if (isMobile) 16.px else 32.px).flexWrap(FlexWrap.Wrap)